import {
  CollaborativeItemType,
  collaborativeItemTypeSchema,
} from '@/types/schema/collaborative-item'
import { PlatformShortcode } from '@/utils/convert-platform'

interface CollaborativeItemDefinition {
  platformShortcode: PlatformShortcode
  items: {
    type: CollaborativeItemType
    category: 'post' | 'video' | 'ad_authorization' | 'material_authorization' // 不同類型有不同的判斷公式
    hasCrawlerData: boolean // DC 爬蟲是否支援
    supportPriceEstimation: boolean // 是否支援 AI 預估價格
    performanceEditable: {
      like: boolean
      comment: boolean
      share: boolean
      view: boolean
      reach: boolean
      linkClick: boolean
    }
  }[]
}

const authorizationItemsDefinition: CollaborativeItemDefinition['items'] = [
  {
    category: 'ad_authorization',
    type: collaborativeItemTypeSchema.enum.ad_authorization,
    hasCrawlerData: false,
    supportPriceEstimation: true,
    performanceEditable: {
      like: true,
      comment: true,
      share: true,
      view: true,
      reach: true,
      linkClick: true,
    },
  },
  {
    category: 'material_authorization',
    type: collaborativeItemTypeSchema.enum.material_authorization,
    hasCrawlerData: false,
    supportPriceEstimation: true,
    performanceEditable: {
      like: true,
      comment: true,
      share: true,
      view: true,
      reach: true,
      linkClick: true,
    },
  },
]

const collaborativeItemDefinitionInstagram: CollaborativeItemDefinition = {
  platformShortcode: PlatformShortcode.Instagram,
  items: [
    {
      category: 'post',
      type: collaborativeItemTypeSchema.enum.post,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.reels,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.live,
      hasCrawlerData: false,
      supportPriceEstimation: false,
      performanceEditable: {
        like: true,
        comment: true,
        share: true,
        view: true,
        reach: true,
        linkClick: true,
      },
    },
    {
      category: 'post',
      type: collaborativeItemTypeSchema.enum.story,
      hasCrawlerData: false,
      supportPriceEstimation: false,
      performanceEditable: {
        like: true,
        comment: true,
        share: true,
        view: false,
        reach: true,
        linkClick: true,
      },
    },
    ...authorizationItemsDefinition,
  ],
}

const collaborativeItemDefinitionFacebook: CollaborativeItemDefinition = {
  platformShortcode: PlatformShortcode.Facebook,
  items: [
    {
      category: 'post',
      type: collaborativeItemTypeSchema.enum.post,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.video,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.live,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'post',
      type: collaborativeItemTypeSchema.enum.story,
      hasCrawlerData: false,
      supportPriceEstimation: false,
      performanceEditable: {
        like: true,
        comment: true,
        share: true,
        view: false,
        reach: true,
        linkClick: true,
      },
    },
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.reels,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    ...authorizationItemsDefinition,
  ],
}

const collaborativeItemDefinitionYouTube: CollaborativeItemDefinition = {
  platformShortcode: PlatformShortcode.YouTube,
  items: [
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.video,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.live,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'video',
      type: collaborativeItemTypeSchema.enum.shorts,
      hasCrawlerData: true,
      supportPriceEstimation: true,
      performanceEditable: {
        like: false,
        comment: false,
        share: false,
        view: false,
        reach: false,
        linkClick: false,
      },
    },
    {
      category: 'post',
      type: collaborativeItemTypeSchema.enum.post,
      hasCrawlerData: false,
      supportPriceEstimation: false,
      performanceEditable: {
        like: true,
        comment: true,
        share: false,
        view: false,
        reach: true,
        linkClick: true,
      },
    },
    ...authorizationItemsDefinition,
  ],
}

const collaborativeItemDefinitionMap = {
  [PlatformShortcode.Instagram]: collaborativeItemDefinitionInstagram,
  [PlatformShortcode.Facebook]: collaborativeItemDefinitionFacebook,
  [PlatformShortcode.YouTube]: collaborativeItemDefinitionYouTube,
}

const collaborativeItemDefinitionList = [
  collaborativeItemDefinitionInstagram,
  collaborativeItemDefinitionFacebook,
  collaborativeItemDefinitionYouTube,
]

export {
  collaborativeItemDefinitionFacebook,
  collaborativeItemDefinitionInstagram,
  collaborativeItemDefinitionList,
  collaborativeItemDefinitionMap,
  collaborativeItemDefinitionYouTube,
}
export type { CollaborativeItemDefinition }
